import {Cell, flexRender, Row} from '@tanstack/react-table';
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';

import {useContextMenu} from '@ELib/functions/contextMenu';
import {classy} from '@ELib/utilities/HelperFunctions';
import {canExecuteAction} from '@ELib/utilities/actions';
import {TFunction} from '@ELib/utilities/i18next';

import {TBodySubRows} from './TBodySubRows';
import {
  ETableActiveItem,
  ETableCellMode,
  ETableRow,
  ETableRowActionHandler,
  ETableRowClickHandler,
  ETableRowDragHandler,
  isCellObject,
} from './types';
import {isActiveItem} from './utilities';

// code moved from webapp, disable conflicting rules in webapp-lib for now.
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

export const NOT_FOUND = '—';

export function TBodyRow({
  row,
  parentRow,
  subRowIndex,
  isSelected = false,
  forceCheckboxes = false,
  activeItem,
  isDraggable,
  onRowClick,
  onRowDragStart,
  onRowDragEnd,
  actions,
  measureRef,
  dataIndex,
}: {
  row: Row<ETableRow>;
  parentRow?: Row<ETableRow>;
  subRowIndex?: number;
  isSelected?: boolean;
  forceCheckboxes?: boolean;
  activeItem: ETableActiveItem | undefined;
  isDraggable: boolean;
  onRowClick: ETableRowClickHandler;
  onRowDragStart?: ETableRowDragHandler;
  onRowDragEnd?: ETableRowDragHandler;
  actions?: ETableRowActionHandler;
  /** Ref callback that measures the rendered row height for virtualization. */
  measureRef?: (element: HTMLTableRowElement | null) => void;
  /** Index of the row within the virtualized data window. */
  dataIndex?: number;
}): React.ReactElement {
  const rowActions = useMemo(() => actions?.(row) ?? [], [actions, row]);

  const {t} = useTranslation('common');

  const disableDoubleClickAction =
    row.original.options?.disableDoubleClickAction;

  const onRowDoubleClick = useCallback(() => {
    const [action] = rowActions;

    if (disableDoubleClickAction || !action) {
      return;
    }

    (async () => {
      if (await canExecuteAction(action, row.original.raw)) {
        action.callback?.(row.original.raw);
      }
    })();
  }, [disableDoubleClickAction, row.original.raw, rowActions]);

  const {contextMenuCallbacks, contextMenu} = useContextMenu(
    rowActions,
    row.original.raw
  );

  const styleType = row.original.options?.styleType;

  /** Whether a row can be activated (i.e. it changes its styling on click). */
  const isActivatable = styleType !== 'info' && styleType !== 'header';

  const onClick = useCallback(
    (evt: React.MouseEvent) => {
      if (!isActivatable) {
        return;
      }
      if (parentRow) {
        onRowClick(evt, parentRow, row);
      } else {
        onRowClick(evt, row);
      }
    },
    [isActivatable, onRowClick, parentRow, row]
  );

  const onContextMenu: MouseEventHandler<HTMLTableRowElement> = useCallback(
    (event) => {
      if (!isSelected) {
        onClick(event);
      }
      if (rowActions.length) {
        contextMenuCallbacks.onContextMenu(event);
        event.stopPropagation();
      }
    },
    [isSelected, rowActions, onClick, contextMenuCallbacks]
  );

  const onDragStart = useCallback(
    (dragEvent: React.DragEvent<HTMLTableRowElement>) => {
      onRowDragStart?.({dragEvent, activeRow: row});
    },
    [onRowDragStart, row]
  );

  const onDragEnd = useCallback(
    (dragEvent: React.DragEvent<HTMLTableRowElement>) =>
      onRowDragEnd?.({dragEvent}),
    [onRowDragEnd]
  );

  const isSubRow = subRowIndex !== undefined;

  const rowElementRef = useRef<HTMLTableRowElement | null>(null);
  const measureRowRef = useCallback(
    (element: HTMLTableRowElement | null) => {
      rowElementRef.current = element;
      measureRef?.(element);
    },
    [measureRef]
  );

  // Re-measure when the (async) expansion content changes: the virtualizer's
  // resize observer only watches this row's own element.
  const remeasure = useCallback(() => {
    if (rowElementRef.current) {
      measureRef?.(rowElementRef.current);
    }
  }, [measureRef]);

  const showSubRows =
    !isSubRow &&
    Boolean(row.original.options?.expansion) &&
    row.getIsExpanded();

  /** Counts the content changes of the expansion, i.e. its new sub rows. */
  const [subRowsGeneration, setSubRowsGeneration] = useState(0);
  const handleSubRowsResized = useCallback(() => {
    remeasure();
    setSubRowsGeneration((generation) => generation + 1);
  }, [remeasure]);

  // The virtualizer's resize observer only watches this row's own element, so
  // watch the sub rows as well: their content can grow after they rendered,
  // e.g. when a table inside the expansion loads its rows.
  useEffect(() => {
    const rowElement = rowElementRef.current;
    if (!showSubRows || !measureRef || !rowElement) {
      return;
    }
    const observer = new ResizeObserver(remeasure);
    for (
      let sibling = rowElement.nextElementSibling;
      sibling instanceof HTMLElement &&
      sibling.dataset.parentId === row.original.id;
      sibling = sibling.nextElementSibling
    ) {
      observer.observe(sibling);
    }
    return () => observer.disconnect();
    // the generation re-arms the observer on the sub rows that replaced the
    // observed ones
  }, [showSubRows, measureRef, remeasure, row.original.id, subRowsGeneration]);

  // Re-measure when the expansion collapses: TBodySubRows unmounts without
  // resizing this row's element, so the virtualizer would otherwise keep the
  // expanded height.
  useEffect(() => {
    remeasure();
  }, [showSubRows, remeasure]);

  // A sub row is a shallow copy of its parent whose methods still act on the
  // parent, so read the cells from the real parent row.
  const cells = (parentRow ?? row).getVisibleCells();

  // Compute all values up front: the colspan calculation needs the whole
  // row. Sub rows resolve their values against their own data.
  const cellValues = cells.map((cell) =>
    isSubRow
      ? cell.column.accessorFn?.(row.original, subRowIndex)
      : cell.getValue()
  );
  const cellsForColSpan = cellValues.map((value) => ({value}));

  return (
    <>
      <tr
        ref={measureRef ? measureRowRef : undefined}
        className={classy({
          noHover:
            !isActivatable ||
            row.original.options?.interactionRestrictionLevel ===
              'noHoverEffect',
          active:
            isActivatable &&
            row.original.options?.interactionRestrictionLevel !==
              'notActivatable' &&
            row.original.options?.interactionRestrictionLevel !==
              'noHoverEffect' &&
            isActiveItem(row.original.id, activeItem),
          selected: isSelected && row.original.options?.isSelectable !== false,
          forceCheckboxes,
          styleInfo: styleType === 'info',
          styleImportant: styleType === 'important',
          styleWarning: styleType === 'warning',
          styleDanger: styleType === 'danger',
          styleHiddenBorderBottom: styleType === 'hide-border-bottom',
          styleDisabled: styleType === 'disabled',
          styleHeader: styleType === 'header',
          styleBold: styleType === 'bold',
        })}
        data-id={row.original.id}
        data-index={dataIndex}
        data-parent-id={isSubRow ? parentRow?.original.id : undefined}
        draggable={isDraggable}
        role='row'
        onClick={onClick}
        onContextMenu={onContextMenu}
        onDoubleClick={onRowDoubleClick}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
      >
        {contextMenu}
        {cells.map((cell, cellIndex) => {
          const cellValue = cellValues[cellIndex];
          if (cellValue === null) {
            // cells with a `null` value are used with colspan and should not render
            return null;
          }
          const cellObject = isCellObject(cellValue);
          return (
            <td
              key={cell.id}
              className={classy({
                textSelectable: Boolean(cellObject && cellValue.textSelectable),
                alignCenter: cellObject && cellValue.align === 'center',
                alignRight: cellObject && cellValue.align === 'right',
                selection: cell.column.id === '_selection',
                expansion: cell.column.id === '_expander',
                styleHiddenBorderBottom:
                  (cellObject &&
                    cellValue.styleType === 'hide-border-bottom') ||
                  (cell.column.id === '_expander' &&
                    row.original.options?.expansionStyleType ===
                      'hide-border-bottom'),
                styleTotal:
                  (cellObject && cellValue.styleType === 'total') ||
                  (cell.column.id === '_expander' &&
                    row.original.options?.expansionStyleType === 'total'),
                ...getCellModeClasses(cellValue, cell.column.id),
              })}
              colSpan={getColSpan(cellsForColSpan, cellIndex)}
              data-testid={`ETable-cell-${cell.column.id}`}
              role='cell'
            >
              {customCellRenderer(
                cell,
                {
                  value: cellValue,
                  subRow: isSubRow ? row : undefined,
                },
                t
              )}
            </td>
          );
        })}
      </tr>
      {showSubRows ? (
        <TBodySubRows
          activeItem={activeItem}
          parent={row}
          onContentResized={measureRef ? handleSubRowsResized : undefined}
          onRowClick={onRowClick}
        />
      ) : null}
    </>
  );
}

function getCellModeClasses(
  value: any,
  columnId: string
): Record<string, boolean> {
  /** Flag whether the cell is special, e.g. a checkbox or extra action cell. */
  const isSpecialCell = columnId.startsWith('_');
  if (isSpecialCell) {
    return {modeMin: true};
  }
  const cellMode: ETableCellMode =
    (isCellObject(value) && value.mode) || 'truncate';
  return {
    modeMin: cellMode === 'min',
    modeTruncate: cellMode === 'truncate',
    modeBreak: cellMode === 'break',
  };
}

function customCellRenderer(
  cell: Cell<ETableRow, unknown>,
  {value, subRow}: {value: any; subRow?: Row<ETableRow>},
  t: TFunction
) {
  if (value === undefined) {
    if (subRow && cell.column.columnDef.meta?.SubCell) {
      // the current cell is within a sub row and from a special column
      return cell.column.columnDef.meta.SubCell(subRow);
    }
    if (cell.column.id.startsWith('_')) {
      // Special columns (e.g. checkboxes) render themselves. Sub rows never
      // reach here, so the parent-row context this closes over is harmless.
      return flexRender(cell.column.columnDef.cell, cell.getContext());
    }
    // cell value was set to undefined but no corresponding renderer was defined
    return NOT_FOUND;
  }
  const rawCellValue = isCellObject(value) ? value.cell : value;
  if (rawCellValue instanceof Date) {
    return t('dateFormat.withoutTime', {date: rawCellValue});
  }
  return rawCellValue ?? NOT_FOUND;
}

/** Sums the consecutive cells with value `null` that are located directly after the passed `cellIndex`. */
export function getColSpan(cells: {value: any}[], cellIndex: number): number {
  let colSpan = 1;
  for (let index = cellIndex + 1; index < cells.length; index++) {
    if (cells[index]!.value === null) {
      colSpan += 1;
    } else {
      break;
    }
  }
  return colSpan;
}
